import React, { useMemo } from 'react';
import { Stack } from '@mui/material';

// Module imports following the 6-module pattern
import { useModelDataSampleListHandlers } from './ModelDataSampleListEventHandlers';
import { useModelDataSampleListStyles } from './ModelDataSampleListStyles';
import { ModelDataSampleListFilter } from './ModelDataSampleListFilter';
import { CellFormatContext } from './ModelDataSampleListUtils';
import {
    HeaderComponent,
    FilterControls,
    LoadingComponent,
    NoDataComponent,
    StatusBadge,
    CommentIcon,
    CellContent,
    DataTable,
    ColumnMenu,
    ColumnManagerDialog,
    PaginationComponent
} from './ModelDataSampleListComponents';

/**
 * ModelDataSampleList component - Refactored using modular architecture
 * Model data sample list component for displaying the data samples
 */
export const ModelDataSampleList: React.FC<{}> = () => {
    const styles = useModelDataSampleListStyles();
    
    // Use the custom hook that manages all state and event handlers
    const {
        // State
        componentState,
        preferredUnits,
        filterState,
        
        // Navigation data
        state,
        
        // Event handlers
        handleRowClick,
        handlePageChange,
        handleChangeRowsPerPage,
        handleColumnFilterChange,
        handleSortMenuClick,
        handleOpenColumnMenu,
        handleCloseColumnMenu,
        handleOpenColumnManager,
        handleCloseColumnManager,
        toggleColumnVisibility,
        toggleFilters,
        navigateBack,
        handleFilterChange,
        handleApplyFilter,
        handleResetFilter,
        
        // Data processing
        getSortedAndFilteredData,
        getVisibleColumns,
        ensureStatusColumnFirst,
    } = useModelDataSampleListHandlers();

    // Build format context from the active unit configuration so that
    // CellContent can delegate to the shared UnitsHelper.formatUnitValue
    // (handles metric<->US conversion, unitOperationTargetValue unit, etc.).
    const formatContext: CellFormatContext = useMemo(() => ({
        flowType: state.unitInputConfig?.flowType,
        unitOperationTargetValueUnit:
            componentState.configurations?.[0]?.unitOperationTargetValueUnit ??
            state.unitInputConfig?.unitOperationTargetValueUnit,
    }), [
        state.unitInputConfig?.flowType,
        state.unitInputConfig?.unitOperationTargetValueUnit,
        componentState.configurations,
    ]);

    // Render cell content with proper formatting
    const renderCellContent = (column: any, row: any) => {
        if (column.id === 'status') {
            return <StatusBadge status={row.dataSampleStatus} />;
        }
        if (column.id === 'modelerComment' || column.id === 'smeComment') {
            return <CommentIcon comment={(row as any)[column.id]} />;
        }
        if (column.id === 'id') {
            return row.id;
        }

        return (
            <CellContent
                columnKey={column.id}
                value={(row as any)[column.id]}
                formatContext={formatContext}
            />
        );
    };

    // Ensure status column is present and first in the visible columns
    const visibleColumns = ensureStatusColumnFirst(getVisibleColumns());

    return (
        <Stack spacing={3} sx={styles.pageContainer}>
            {/* Header section */}
            <HeaderComponent
                title={state.alfaLavalPosition}
                productLine={state.productLine}
                onBackClick={navigateBack}
            />
            
            {/* Filter controls */}
            <FilterControls
                showFilters={componentState.showFilters}
                isLoading={componentState.isLoading}
                onToggleFilters={toggleFilters}
            />

            {/* Advanced filtering */}
            {componentState.showFilters && (
                <ModelDataSampleListFilter
                    filterState={filterState}
                    onFilterChange={handleFilterChange}
                    onApplyFilter={handleApplyFilter}
                    onResetFilter={handleResetFilter}
                    unitConfig={state.unitInputConfig}
                    preferredUnits={preferredUnits}
                />
            )}

            {/* Loading state */}
            {componentState.isLoading ? (
                <LoadingComponent />
            ) : (
                <>
                    {/* Data table */}
                    {componentState.currentData && 
                     componentState.currentData.length > 0 && 
                     componentState.dataColumns.length > 0 ? (
                        <>
                            <DataTable
                                columns={visibleColumns}
                                data={getSortedAndFilteredData()}
                                onRowClick={handleRowClick}
                                onMenuClick={handleOpenColumnMenu}
                                renderCellContent={renderCellContent}
                            />
                            
                            {/* Column Menu */}
                            <ColumnMenu
                                anchorEl={componentState.columnMenuAnchor}
                                open={Boolean(componentState.columnMenuAnchor)}
                                onClose={handleCloseColumnMenu}
                                onSort={handleSortMenuClick}
                                onHideColumn={() => toggleColumnVisibility(componentState.activeColumn)}
                                onManageColumns={handleOpenColumnManager}
                            />
                            
                            {/* Column Manager Dialog */}
                            <ColumnManagerDialog
                                open={componentState.columnManagerOpen}
                                columns={componentState.dataColumns}
                                hiddenColumns={componentState.hiddenColumns}
                                onClose={handleCloseColumnManager}
                                onToggleColumn={toggleColumnVisibility}
                            />

                            {/* Pagination */}
                            <PaginationComponent
                                count={componentState.pageCount * componentState.rowsPerPage}
                                page={componentState.currentPage}
                                rowsPerPage={componentState.rowsPerPage}
                                onPageChange={handlePageChange}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </>
                    ) : (
                        /* No data state */
                        <NoDataComponent />
                    )}
                </>
            )}
        </Stack>
    );
}; 